/**
 * NotificationSettings - Configure notification preferences
 *
 * Settings for audio and browser push notifications when AI responds.
 */

import { useState } from 'react';
import { Bell, Volume2, VolumeX } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import { Slider } from '../ui/slider';
import { Button } from '../ui/button';
import { useSettingsStore } from '../../stores/settingsStore';
import { testNotificationSound } from '../../hooks/useCompletionNotification';

export function NotificationSettings() {
  const {
    notificationSoundEnabled,
    notificationPushEnabled,
    notificationVolume,
    setNotificationSoundEnabled,
    setNotificationPushEnabled,
    setNotificationVolume,
  } = useSettingsStore();

  const [pushPermission, setPushPermission] = useState<NotificationPermission>(
    typeof Notification !== 'undefined' ? Notification.permission : 'denied'
  );

  // Request notification permission
  const requestPermission = async () => {
    if (!('Notification' in window)) return;

    const permission = await Notification.requestPermission();
    setPushPermission(permission);

    if (permission === 'granted') {
      setNotificationPushEnabled(true);
    }
  };

  // Handle push toggle
  const handlePushToggle = async (checked: boolean) => {
    if (checked && pushPermission !== 'granted') {
      await requestPermission();
    } else {
      setNotificationPushEnabled(checked);
    }
  };

  // Test sound
  const handleTestSound = () => {
    testNotificationSound(notificationVolume);
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center gap-2">
          <Bell className="h-5 w-5 text-primary" />
          <CardTitle>Notifications</CardTitle>
        </div>
        <CardDescription>
          Get notified when AI finishes responding while you're away
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Sound Notification Toggle */}
        <div className="flex items-center justify-between">
          <div className="space-y-0.5">
            <Label htmlFor="sound-toggle" className="text-base font-medium">
              Sound notification
            </Label>
            <p className="text-sm text-muted-foreground">
              Play a sound when AI response is ready
            </p>
          </div>
          <Switch
            id="sound-toggle"
            checked={notificationSoundEnabled}
            onCheckedChange={setNotificationSoundEnabled}
          />
        </div>

        {/* Volume Slider - only show when sound is enabled */}
        {notificationSoundEnabled && (
          <div className="space-y-3 pl-1">
            <div className="flex items-center justify-between">
              <Label className="text-sm text-muted-foreground flex items-center gap-2">
                {notificationVolume === 0 ? (
                  <VolumeX className="h-4 w-4" />
                ) : (
                  <Volume2 className="h-4 w-4" />
                )}
                Volume
              </Label>
              <span className="text-sm text-muted-foreground">
                {Math.round(notificationVolume * 100)}%
              </span>
            </div>
            <div className="flex items-center gap-3">
              <Slider
                value={[notificationVolume]}
                onValueChange={([value]) => setNotificationVolume(value)}
                min={0}
                max={1}
                step={0.1}
                className="flex-1"
              />
              <Button
                variant="outline"
                size="sm"
                onClick={handleTestSound}
                className="shrink-0"
              >
                Test
              </Button>
            </div>
          </div>
        )}

        {/* Browser Notification Toggle */}
        <div className="flex items-center justify-between pt-2 border-t">
          <div className="space-y-0.5">
            <Label htmlFor="push-toggle" className="text-base font-medium">
              Browser notification
            </Label>
            <p className="text-sm text-muted-foreground">
              Show a popup notification (requires permission)
            </p>
          </div>
          <Switch
            id="push-toggle"
            checked={notificationPushEnabled && pushPermission === 'granted'}
            onCheckedChange={handlePushToggle}
            disabled={pushPermission === 'denied'}
          />
        </div>

        {/* Permission status message */}
        {pushPermission === 'denied' && (
          <p className="text-xs text-amber-600 dark:text-amber-400 pl-1">
            Browser notifications are blocked. Enable them in your browser settings.
          </p>
        )}

        {/* Info note */}
        <div className="text-xs text-muted-foreground bg-muted/50 rounded-lg p-3 mt-4">
          Notifications only trigger when your browser tab is not focused.
        </div>
      </CardContent>
    </Card>
  );
}
